import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import { ClFarmStore } from "@/domains/cl-farm"
import { unstakeClFarmLiquidity } from "@/domains/on-chain"

type UnstakeClFarmLiquidityInteraction = {
  amount: bigint
  chainId: number
  clFarm: ClFarmStore
  config: Config
  connectedAddress: Address
  emitter: EventEmitter<EmitEvent>
  onSuccess: () => void
}

export const unstakeClFarmLiquidityInteraction = async ({
  amount,
  chainId,
  clFarm,
  config,
  connectedAddress,
  emitter,
  onSuccess,
}: UnstakeClFarmLiquidityInteraction) => {
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }
  if (!clFarm) return

  // STEP ONE: Check if the user has enough balance in the farm to withdraw
  if (clFarm.onChainUserGaugeBalance < amount) {
    const overspendAmount = amount - clFarm.onChainUserGaugeBalance
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.displayName} in your wallet. You are trying to withdraw ${clFarm.formatUnits(overspendAmount)} more than available.`,
      variant: "error",
    })
    return
  }

  // STEP TWO: Withdraw the Liquidity from the Farm
  const unstakeClFarmLiquidityResult = await unstakeClFarmLiquidity({
    account: connectedAddress,
    chainId,
    config,
    farmAddress: clFarm.farmAddress.toViemAddress(),
    amount,
  })

  if (unstakeClFarmLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-unstake-liquidity", {
      title: `Unstake  ${clFarm.displayName}`,
      description: `Unstaked ${clFarm.formatUnits(amount)} ${clFarm.displayName} tokens`,
      variant: "success",
    })
  } else {
    emitter.emit("error-stake-liquidity", {
      title: `Unable to unstake ${clFarm.formatUnits(amount)} ${clFarm.displayName} tokens`,
      description: unstakeClFarmLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }

  return unstakeClFarmLiquidityResult
}
